// A war horse, modelled facing +X: a dark bay build on the v2 horse frame —
// long dark legs with hooves, a deep barrel, an arched neck carrying a refined
// head — dressed in military tack: a khaki saddle blanket and saddle, girth
// and breast harness straps, a simple bridle and a pair of khaki saddlebags.
// The looping "idle" dips the head gently and sways the tail: a patient remount
// waiting at the picket line. Sized to match the existing horse-v2.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const COAT = "#5a3d28";      // dark bay
const SHADE = "#4a3220";     // shaded bay
const MANE = "#2e2218";      // mane / tail / points
const MUZZLE = "#4a3018";
const HOOF = "#16120e";
const KHAKI = "#7a6f4e";     // blanket / saddlebags
const LEATHER = "#3a2a1a";   // straps / saddle seat
const BRASS = "#b08d3a";     // buckles
const EYE = "#1c140e";

// The neck's lean from vertical — the mane crest shares it so they stay parallel.
const NECK_LEAN = 0.62;

function warHorseParts(): Part[] {
  const head = (p: Part): Part => ({ ...p, name: "head" });
  const tail = (p: Part): Part => ({ ...p, name: "tail" });
  const parts: Part[] = [];
  // four dark legs with hooves (black points of a bay)
  for (const [x, z] of [[0.078, 0.03], [0.078, -0.03], [-0.078, 0.03], [-0.078, -0.03]] as const) {
    parts.push(
      { geometry: new THREE.CylinderGeometry(0.0085, 0.006, 0.158, 4), color: MANE, position: [x, 0.079, z] },
      { geometry: new THREE.CylinderGeometry(0.0085, 0.0085, 0.015, 4), color: HOOF, position: [x, 0.0075, z] },
    );
  }
  parts.push(
    // barrel with a deep chest and rounded croup
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: COAT, position: [-0.002, 0.158, 0], scale: [1.7, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: SHADE, position: [0.07, 0.152, 0], scale: [1.0, 1.05, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: COAT, position: [-0.075, 0.165, 0], scale: [1.05, 0.95, 0.85] },
    // khaki saddle blanket over the back, leather saddle seat on top
    { geometry: new THREE.BoxGeometry(0.075, 0.012, 0.105), color: KHAKI, position: [0.005, 0.208, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.016, 0.055), color: LEATHER, position: [0.005, 0.221, 0] },
    { geometry: new THREE.BoxGeometry(0.012, 0.014, 0.05), color: LEATHER, position: [0.028, 0.231, 0] }, // pommel rise
    // girth strap wrapping under the barrel and breast strap across the chest
    { geometry: new THREE.BoxGeometry(0.014, 0.095, 0.098), color: LEATHER, position: [0.005, 0.158, 0] },
    { geometry: new THREE.BoxGeometry(0.012, 0.014, 0.075), color: LEATHER, position: [0.098, 0.17, 0] },
    { geometry: new THREE.BoxGeometry(0.01, 0.01, 0.01), color: BRASS, position: [0.098, 0.17, 0] },
    // khaki saddlebags hanging behind the saddle on both flanks
    { geometry: new THREE.BoxGeometry(0.045, 0.05, 0.02), color: KHAKI, position: [-0.055, 0.16, 0.048], rotation: [0, 0, 0.08] },
    { geometry: new THREE.BoxGeometry(0.045, 0.05, 0.02), color: KHAKI, position: [-0.055, 0.16, -0.048], rotation: [0, 0, 0.08] },
    { geometry: new THREE.BoxGeometry(0.048, 0.01, 0.022), color: LEATHER, position: [-0.055, 0.183, 0.048] },
    { geometry: new THREE.BoxGeometry(0.048, 0.01, 0.022), color: LEATHER, position: [-0.055, 0.183, -0.048] },
    // the arched neck rising steeply to the poll (dips with the head)
    head({ geometry: new THREE.CylinderGeometry(0.018, 0.03, 0.105, 5), color: COAT, position: [0.105, 0.205, 0], rotation: [0, 0, -NECK_LEAN] }),
    // mane crest along the neck's back edge
    head({ geometry: new THREE.BoxGeometry(0.014, 0.115, 0.013), color: MANE, position: [0.0855, 0.219, 0.005], rotation: [0, 0, -NECK_LEAN] }),
    // refined head angled down-forward, tapered muzzle, forelock
    head({ geometry: new THREE.BoxGeometry(0.05, 0.03, 0.026), color: COAT, position: [0.156, 0.252, 0], rotation: [0, 0, -0.35] }),
    head({ geometry: new THREE.ConeGeometry(0.0115, 0.032, 5), color: MUZZLE, position: [0.185, 0.243, 0], rotation: [0, 0, -Math.PI / 2 - 0.35] }),
    head({ geometry: new THREE.IcosahedronGeometry(0.008, 0), color: MANE, position: [0.162, 0.272, 0] }),
    // simple bridle: noseband and cheek strap
    head({ geometry: new THREE.BoxGeometry(0.008, 0.012, 0.03), color: LEATHER, position: [0.171, 0.248, 0], rotation: [0, 0, -0.35] }),
    head({ geometry: new THREE.BoxGeometry(0.026, 0.008, 0.03), color: LEATHER, position: [0.15, 0.259, 0], rotation: [0, 0, -0.35] }),
    // pricked ears and eyes
    head({ geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: SHADE, position: [0.147, 0.272, 0.011], rotation: [0.15, 0, 0.1] }),
    head({ geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: SHADE, position: [0.147, 0.272, -0.011], rotation: [-0.15, 0, 0.1] }),
    head({ geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: EYE, position: [0.155, 0.26, 0.0145] }),
    head({ geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: EYE, position: [0.155, 0.26, -0.0145] }),
    // high-set tail sweeping back-down into a full plume
    tail({ geometry: new THREE.CylinderGeometry(0.0085, 0.0065, 0.05, 4), color: MANE, position: [-0.127, 0.1535, 0], rotation: [0, 0, 2.29] }),
    tail({ geometry: new THREE.IcosahedronGeometry(0.016, 0), color: MANE, position: [-0.148, 0.112, 0], scale: [1.2, 1.9, 0.7] }),
  );
  return parts;
}

export const warHorse = defineModel({
  id: "war-horse",
  scale: 2.3, // real-world size correction, matching horse-v2 (see Model.scale)
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "War Horse",
  tags: ["creature", "animal", "horse", "wwi", "military", "animated"],
  summary: "A dark bay army remount in khaki tack with saddlebags.",
  description:
    "A dark bay war horse in military tack: khaki saddle blanket and " +
    "saddlebags, a leather saddle, girth and breast straps and a simple " +
    "bridle. Standing at rest; the looping idle dips its head gently and " +
    "sways the tail.",
  author: "Fable 5",
  footprint: 0.16,
  fits: ["draft"],
  parts: warHorseParts,
  animations: [
    {
      id: "idle",
      label: "Idle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          // gentle head dip about the base of the neck
          target: "head",
          pivot: [0.09, 0.16, 0],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 1.6, value: [0, 0, -0.09], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // tail swishing side to side from the dock
          target: "tail",
          pivot: [-0.115, 0.17, 0],
          rotation: [
            { t: 0, value: [0, 0.22, 0] },
            { t: 1.6, value: [0, -0.22, 0], ease: "sine" },
            { t: 3.2, value: [0, 0.22, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
